/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { padding } from 'polished';
import { isEmpty } from 'lodash/fp';
import { plural } from 'pluralize';

import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import ConcealedButton from 'app/components/commons/ConcealedButton';
import RadioButton from 'app/components/commons/RadioButton';
import Checkbox from 'app/components/commons/Checkbox';
import LiveChallengeFilterContent from 'app/components/challenge/LiveChallengeFilterContent';
import OpenChallengeFilterContent from 'app/components/challenge/OpenChallengeFilterContent';
import PlayerSelectionSortChoice from 'app/components/challenge/PlayerSelectionSortChoice';
import PlayerSearchBar from 'app/components/challenge/PlayerSearchBar';
import Responsive from 'app/components/responsive/Responsive';
import SortIndicator from 'app/components/challenge/SortIndicator';
import Tappable from 'app/components/commons/Tappable';
import isFilteredSelector from 'app/selectors/isFilteredSelector';
import {
  resetFilters,
  setSortField,
  setPlayerTypeFilter,
  toggleAutoAdvancePositionsFilter,
  toggleProjectedScoreFilter,
  toggleTeamsFilter
} from 'app/actions/playerListActions';
import t from 'app/utils/translate';
import { IN_PROGRESS } from 'app/constants/challengeStatusTypes';
import { hover } from 'app/utils/cssUtils';
import FiltersToggleButton from 'app/components/commons/FiltersToggleButton';
import ExportPlayers from 'app/components/challenge/ExportPlayers';
import { isChallengeMLB, isChallengeNBA } from 'app/utils/challengeUtils';
import Icon from '../commons/Icon';
import OddsProvidedBy from './OddsProvidedBy';
import StarSymbol from './StarSymbol';

type Props = {
  autoAdvancePositions: boolean,
  games: Array<*>,
  hasTeams: boolean,
  isFiltered: boolean,
  isNBA: boolean,
  isMLB: boolean,
  isRacing: boolean,
  onReset: () => void,
  onSort: string => void,
  racingType?: string,
  positionFilter?: string,
  showOddsProvider?: boolean,
  sortDirection: 'asc' | 'desc',
  sortField?: string,
  statusId: number,
  onIncludedChange: boolean => void,
  onTeamsChange: () => void,
  onToggleAutoAdvancePositions: () => void,
  onToggleProjectedScore: () => void,
  playerTypeFilters: Array<string>,
  filteredTeams: Array<string>,
  showProjectedScore: boolean,
  showProjectedScoreToggler?: boolean
};

type State = {
  content?: 'filters' | 'search' | 'sort'
};

const FilterBar = styled(AnimatedGroup)(({ theme }) => ({
  backgroundColor: theme.color.basis6,
  color: theme.color.basis1,
  overflow: 'hidden',
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: '0.8rem'
  }
}));

const FilterBarTop = styled.div(({ theme }) => ({
  alignItems: 'stretch',
  display: 'flex',
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: '0.8rem',
    paddingLeft: 5
  }
}));

const FilterBarControls = styled.div({
  alignItems: 'stretch',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0, // flexbox hack
  textTransform: 'uppercase'
});

const Title = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  padding: '0.375em 0.5em',
  whiteSpace: 'nowrap'
});

const PlayerSelectionSearch = styled.div(
  {
    alignItems: 'stretch',
    display: 'flex',
    flexBasis: 0,
    flexGrow: 0,
    flexShrink: 0,
    borderLeft: '1px solid',
    borderRight: '1px solid'
  },
  ({ theme }) => ({
    borderColor: theme.color.basis5
  })
);

const PlayerSortButton = styled.div(({ selected }) => ({
  backgroundColor: selected ? 'white' : undefined,
  borderRight: '1px solid #d9d9d9',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: 0, // flexbox hack
  ...padding('0.375em', 10)
}));

const PlayerSelectionMenuIcon = styled(({ className, expanded }) => (
  <span className={`${className} ${expanded ? 'ion-chevron-up' : 'ion-chevron-down'}`} />
))({
  paddingLeft: '0.5em',
  fontSize: '0.7em'
});

const PlayerSearchButton = styled('button')(({ selected, theme }) => ({
  backgroundColor: selected ? 'white' : undefined,
  color: theme.color.basis3,
  fontSize: '1.25rem',
  ...hover({
    backgroundColor: 'white'
  }),
  outline: 'none',
  ...padding('0.375em', 10)
}));

const StyledFiltersToggleButton = styled(FiltersToggleButton)(({ theme }) => ({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  borderRight: '1px solid #d9d9d9',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    textAlign: 'right',
    borderRight: 'none'
  }
}));

const PaddedIcon = styled(Icon)(({ theme }) => ({
  paddingRight: theme.spacing.tiny,
  fontSize: '1.2em'
}));

const StyledExportPlayers = styled(ConcealedButton)({
  ...padding('0.375em', 10),
  textTransform: 'uppercase',
  ...hover({
    backgroundColor: 'white'
  })
});

const PlayerFilter = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 0,
  flexShrink: 1,
  padding: '0.375em 0.5em',
  whiteSpace: 'nowrap'
});

const Clickable = styled.div({
  display: 'flex',
  alignItems: 'center'
});

class PlayerSelectionFilterBar extends React.Component<Props, State> {
  props: Props;

  state: State = {
    content: undefined
  };

  static defaultProps = {
    racingType: undefined,
    positionFilter: undefined,
    showOddsProvider: false,
    showProjectedScoreToggler: false,
    sortField: undefined
  };

  componentDidMount() {
    const {
      autoAdvancePositions,
      isMLB,
      isNBA,
      isRacing,
      onIncludedChange,
      onToggleAutoAdvancePositions,
      onToggleProjectedScore,
      playerTypeFilters,
      showProjectedScore
    } = this.props;

    if (isRacing && !showProjectedScore) {
      onToggleProjectedScore(); // show projected score by default for horse racing
    }

    if (isRacing && !autoAdvancePositions) {
      onToggleAutoAdvancePositions();
    }

    if (!playerTypeFilters.includes('included') && !isNBA && !isMLB && !isRacing) {
      onIncludedChange();
    }
  }

  toggleFilters = () => {
    this.setState(state => ({
      content: state.content !== 'filters' ? 'filters' : undefined
    }));
  };

  toggleSearch = () => {
    this.setState(state => ({
      content: state.content !== 'search' ? 'search' : undefined
    }));
  };

  toggleSort = () => {
    this.setState(state => ({
      content: state.content !== 'sort' ? 'sort' : undefined
    }));
  };

  toggleAutoAdvancePositions = () => {
    const { onToggleAutoAdvancePositions } = this.props;

    onToggleAutoAdvancePositions();
  };

  toggleProjectedScore = () => {
    const { onToggleProjectedScore } = this.props;

    onToggleProjectedScore();
  };

  render() {
    const {
      autoAdvancePositions,
      filteredTeams,
      isFiltered,
      isRacing,
      onReset,
      onSort,
      onIncludedChange,
      racingType,
      playerTypeFilters,
      positionFilter,
      showOddsProvider,
      showProjectedScore,
      showProjectedScoreToggler,
      sortDirection,
      sortField,
      statusId,
      games,
      hasTeams,
      onTeamsChange
    } = this.props;

    const { content } = this.state;

    return (
      <Responsive tabletLandscape desktop>
        {isLargeScreen => (
          <FilterBar>
            <FilterBarTop>
              <FilterBarControls>
                {isLargeScreen && (
                  <Title>
                    {positionFilter ||
                      t(`All ${isRacing && racingType ? plural(racingType) : 'Players'}`)}
                  </Title>
                )}
                {!isLargeScreen && (
                  <Title>
                    <Tappable component={Clickable} onTap={onIncludedChange}>
                      <RadioButton checked={playerTypeFilters.includes('included')} />
                      {'\u00a0\u00a0'}
                      {isRacing ? 'Hide Scratched' : 'Hide Inactive'}
                    </Tappable>
                  </Title>
                )}
                {isLargeScreen && (
                  <React.Fragment>
                    {showProjectedScoreToggler && (
                      <PlayerFilter>
                        <Checkbox checked={showProjectedScore} onChange={this.toggleProjectedScore}>
                          {t('Projections')}
                          <StarSymbol />
                        </Checkbox>
                      </PlayerFilter>
                    )}
                    <PlayerFilter>
                      <Checkbox
                        checked={playerTypeFilters.includes('included')}
                        onChange={onIncludedChange}
                      >
                        {t(`${isRacing ? 'Hide scratched' : 'Hide inactive players'}`)}
                      </Checkbox>
                    </PlayerFilter>
                    {statusId < IN_PROGRESS && (
                      <PlayerFilter>
                        <Checkbox
                          checked={autoAdvancePositions}
                          onChange={this.toggleAutoAdvancePositions}
                        >
                          {t('Auto advance')}
                        </Checkbox>
                      </PlayerFilter>
                    )}
                  </React.Fragment>
                )}
                {isLargeScreen && (
                  <StyledExportPlayers component={ExportPlayers}>
                    <PaddedIcon type="icon_download" />
                    {isRacing ? 'Horse' : 'Player'} CSV
                  </StyledExportPlayers>
                )}
                <PlayerSelectionSearch>
                  <PlayerSearchButton
                    type="button"
                    selected={content === 'search'}
                    onClick={this.toggleSearch}
                  >
                    <span className="ion-search" />
                  </PlayerSearchButton>
                </PlayerSelectionSearch>
                {(!isRacing || isLargeScreen) && (
                  <StyledFiltersToggleButton
                    isFiltered={isFiltered}
                    isOpen={content === 'filters'}
                    onClick={this.toggleFilters}
                    onClear={onReset}
                    scheme="light"
                  />
                )}
                {isLargeScreen && showOddsProvider && <OddsProvidedBy />}
                {!isLargeScreen && (
                  <Tappable
                    component={PlayerSortButton}
                    onTap={this.toggleSort}
                    selected={content === 'sort'}
                  >
                    <SortIndicator direction={sortDirection} field={sortField} />
                    <PlayerSelectionMenuIcon expanded={content === 'sort'} />
                  </Tappable>
                )}
              </FilterBarControls>
            </FilterBarTop>
            {content === 'filters' &&
              (statusId < IN_PROGRESS ? (
                <OpenChallengeFilterContent
                  filteredTeams={filteredTeams}
                  games={games}
                  hasTeams={hasTeams}
                  onTeamsChange={onTeamsChange}
                />
              ) : (
                <LiveChallengeFilterContent showGamesFilter={!isRacing} />
              ))}
            {!isLargeScreen && content === 'sort' && (
              <PlayerSelectionSortChoice
                isRacing={isRacing}
                onSort={onSort}
                sortField={sortField}
                statusId={statusId}
              />
            )}
            {content === 'search' && <PlayerSearchBar autoFocus onCancel={this.toggleSearch} />}
          </FilterBar>
        )}
      </Responsive>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    autoAdvancePositions: !!state.playerList.autoAdvancePositions,
    hasTeams: !isEmpty(state.challenge.teams),
    filteredTeams: state.playerList.filteredTeams,
    games: state.challenge.games,
    isFiltered: !state.playerList.searchTerm && isFilteredSelector(state),
    isMLB: isChallengeMLB(state.challenge.competitionId),
    isNBA: isChallengeNBA(state.challenge.competitionId),
    racingType: state.challenge.racingType,
    playerTypeFilters: state.playerList.playerTypeFilters,
    showProjectedScore: !!state.playerList.showProjectedScore,
    sortDirection: state.challenge.sortDirection,
    sortField: state.challenge.sortField,
    statusId: state.challenge.statusId
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onIncludedChange: () => dispatch(setPlayerTypeFilter('included')),
    onToggleAutoAdvancePositions: () => dispatch(toggleAutoAdvancePositionsFilter()),
    onToggleProjectedScore: () => dispatch(toggleProjectedScoreFilter()),
    onTeamsChange: teamId => dispatch(toggleTeamsFilter(teamId)),
    onReset: () => dispatch(resetFilters()),
    onSort: sortField => dispatch(setSortField(sortField))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PlayerSelectionFilterBar);
